<script setup lang="ts">
import { ref } from 'vue'
import { useI18n } from 'vue-i18n'
import { useAuth } from '@/composables/useAuth'
import OrganizerNavBar from '@/components/layout/OrganizerNavBar.vue'
import OrganizerSidebar from '@/components/layout/OrganizerSidebar.vue'
import AppFooter from '@/components/layout/AppFooter.vue'
import PayoutAccountBanner from '@/components/organizer/payouts/PayoutAccountBanner.vue'
import GalleryToastHost from '@/components/organizer/galleries/GalleryToastHost.vue'
import GalleryUploadTray from '@/components/organizer/galleries/GalleryUploadTray.vue'

const { t } = useI18n()
const { isEmailVerified } = useAuth()

const sidebarOpen = ref(false)

// Dismissal is per browser session (sessionStorage): the reminder reappears
// on a new tab/session but does not keep nagging within the same one. The
// banner is a reminder only - missing verification never blocks any action
// (see issue #205).
const VERIFY_EMAIL_BANNER_DISMISSED_KEY = 'organizer.verify_email_banner_dismissed'
const verifyEmailBannerDismissed = ref(sessionStorage.getItem(VERIFY_EMAIL_BANNER_DISMISSED_KEY) === 'true')

function dismissVerifyEmailBanner(): void {
  verifyEmailBannerDismissed.value = true
  sessionStorage.setItem(VERIFY_EMAIL_BANNER_DISMISSED_KEY, 'true')
}
</script>

<template>
  <div class="min-h-screen bg-slate-900 flex flex-col">
    <OrganizerNavBar @toggle-sidebar="sidebarOpen = !sidebarOpen" />
    <div class="flex flex-1">
      <OrganizerSidebar :mobile-open="sidebarOpen" @close="sidebarOpen = false" />
      <div
        v-if="sidebarOpen"
        class="fixed inset-0 z-20 bg-black/50 lg:hidden"
        @click="sidebarOpen = false"
      />
      <main class="flex-1 min-w-0">
        <!-- Email verification reminder banner: dismissible, non-blocking (see issue #205) -->
        <div
          v-if="!isEmailVerified && !verifyEmailBannerDismissed"
          class="mx-auto max-w-7xl px-4 sm:px-6 lg:px-8 pt-6 pb-0"
        >
          <div class="rounded-xl bg-amber-500/10 border border-amber-500/30 px-5 py-4 flex items-center justify-between gap-4">
            <p class="text-sm text-amber-400">{{ t('dashboard.organizer.verify_email_banner') }}</p>
            <div class="flex items-center gap-4 shrink-0">
              <RouterLink
                to="/organizer/verify-email"
                class="text-sm font-semibold text-amber-400 hover:text-amber-300 transition-colors"
              >
                {{ t('dashboard.organizer.verify_email_link') }}
              </RouterLink>
              <button
                type="button"
                :aria-label="t('common.close')"
                class="text-amber-400 hover:text-amber-300 transition-colors"
                @click="dismissVerifyEmailBanner"
              >
                <svg class="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M6 18L18 6M6 6l12 12" />
                </svg>
              </button>
            </div>
          </div>
        </div>
        <!-- Payout setup banner: owners only, not dismissible (see issue #237) -->
        <PayoutAccountBanner />
        <slot />
      </main>
    </div>
    <AppFooter variant="compact" />
    <!-- Mounted once so gallery uploads and their tray persist across every organizer route (issue #258) -->
    <GalleryUploadTray />
    <GalleryToastHost />
  </div>
</template>
